import RAPIER from '@dimforge/rapier3d-compat';
import { platformPosAt, type GymBox, type PlatformDef } from '../level/gym';
import type { CollisionService, MoveResult } from '../sim/collision';
import { FIXED_DT } from '../sim/motion';
import type { V3 } from '../sim/vec';
import {
  UCX_NONE_FOUND,
  placementQuat,
  sameScale,
  transformShapes,
  type CollisionShape,
  type CollisionSource,
  type EntityCollisionInfo,
  type PlacementPose,
  type Vec3,
} from '../world/collision';

/**
 * Rapier as a sweep/resolve service ONLY — gravity and all feel decisions live in src/sim/motion.ts.
 * Positions crossing this seam are capsule FEET points; Rapier colliders use the capsule center.
 * The compat build runs in both browser and Node, so tests can use real geometry headlessly.
 */
export interface RapierWorldConfig {
  capsuleRadius: number;
  capsuleHeight: number;
}

/**
 * F1 — a placement's FULL rotation as Rapier's `{x,y,z,w}`. It is a shape adapter over
 * `placementQuat` and nothing else: the composition order is defined once, in `world/collision.ts`,
 * so the rigid body, the collider transform and the rendered mesh cannot disagree about what
 * "pitch 30" means. Yaw-only placements short-circuit inside `placementQuat` to a bit-identical
 * quaternion, so nothing already bound moves.
 */
function placementRotation(pose: PlacementPose): RAPIER.Rotation {
  const [x, y, z, w] = placementQuat(pose);
  return { x, y, z, w };
}

/** The shortest rotation taking +Y onto unit `d` (a capsule's axis): axis = Y × d, w = 1 + Y · d. */
function quatFromYTo(d: V3): RAPIER.Rotation {
  if (d.y < -0.999999) return { x: 1, y: 0, z: 0, w: 0 }; // antiparallel: any axis ⊥ Y
  const x = d.z; // Y × d = (d.z, 0, −d.x)
  const z = -d.x;
  const w = 1 + d.y;
  const n = Math.hypot(x, z, w) || 1;
  return { x: x / n, y: 0, z: z / n, w: w / n };
}

/**
 * ── ⭐ S5 · ONE FIXED BODY PER STATIC-MESH PLACEMENT, N COLLIDERS ──────────────────────────────
 *
 * The body carries the placement's `pos` and `yaw` (a re-pose is `setTranslation`/`setRotation`,
 * measured 0.2 ms); the colliders carry the SCALE baked into their points (`transformShapes` with
 * pos 0 / yaw 0), because a Rapier collider has no scale of its own — which is exactly why an
 * editor move re-poses and a scale edit rebuilds. `scaled` keeps the unposed shapes for the
 * `collision` show flag so the overlay draws what the physics world actually holds.
 */
interface EntityRec {
  body: RAPIER.RigidBody;
  colliders: RAPIER.Collider[];
  info: EntityCollisionInfo;
  /** The scale-baked, unposed shapes (what the colliders were built from) — re-posed per query. */
  scaled: CollisionShape[];
  pose: PlacementPose;
}

interface PlatformRec {
  def: PlatformDef;
  body: RAPIER.RigidBody;
  /** How far the platform moved this tick / its velocity — refreshed by advancePlatforms. */
  delta: V3;
  vel: V3;
}

export class RapierCollisionService implements CollisionService {
  /** Collider handle → moving platform record. */
  private readonly platforms = new Map<number, PlatformRec>();
  /** Collider handle → surface tag (station 11 surface grid). */
  private readonly surfaces = new Map<number, string>();
  /** Every static level collider (the world's authored boxes) — tracked so resetLevel removes them. */
  private readonly levelColliders: RAPIER.Collider[] = [];
  /** Placement index → its collision body (S5). Cleared with the level; rebound by the host. */
  private readonly entities = new Map<number, EntityRec>();
  /** Degenerate-shape refusals warn ONCE per asset+kind (rule 6), not once per placement per rebuild. */
  private readonly hullWarned = new Set<string>();

  private constructor(
    private readonly world: RAPIER.World,
    private readonly controller: RAPIER.KinematicCharacterController,
    private readonly body: RAPIER.RigidBody,
    private readonly collider: RAPIER.Collider,
    private readonly centerOffsetY: number,
  ) {}

  static async create(cfg: RapierWorldConfig): Promise<RapierCollisionService> {
    await RAPIER.init();
    // Zero gravity: the motion model owns gravity; Rapier only resolves our desired translation.
    const world = new RAPIER.World({ x: 0, y: 0, z: 0 });

    const halfHeight = cfg.capsuleHeight / 2 - cfg.capsuleRadius;
    const centerOffsetY = cfg.capsuleHeight / 2;

    const body = world.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(0, centerOffsetY, 0),
    );
    const collider = world.createCollider(
      RAPIER.ColliderDesc.capsule(halfHeight, cfg.capsuleRadius),
      body,
    );

    const controller = world.createCharacterController(0.02);
    controller.setUp({ x: 0, y: 1, z: 0 });
    controller.setMaxSlopeClimbAngle((50 * Math.PI) / 180);
    controller.setMinSlopeSlideAngle((55 * Math.PI) / 180);
    controller.enableAutostep(0.4, 0.2, true);
    controller.enableSnapToGround(0.35);
    controller.setSlideEnabled(true);

    return new RapierCollisionService(world, controller, body, collider, centerOffsetY);
  }

  /*
   * ⭐ AN `addGroundPlane` METHOD USED TO LIVE HERE — `cuboid(120, 0.5, 120)` at y = -0.5, added by
   * `mount.ts` at boot and re-added by `resetLevel` below, under EVERY world. It was a floor no
   * world authored: invisible to the editor's pick set, absent from `world.boxes`, and — because
   * `resetLevel` re-added it — the direct cause of the F2→Play bug `mount.ts` records, where the
   * player SANK to y = 0 and walked through Sponza's walls. The floor is a `world.boxes` entry
   * now (top face at exactly y = 0), so it arrives through `addBox` like every other solid and
   * this service has exactly one way to be told what the level is made of.
   */

  /** Axis-aligned box, optionally rotated about Z (ramps). Sizes are FULL extents. */
  addBox(center: V3, size: V3, rotZDeg = 0, surface?: string): void {
    const desc = RAPIER.ColliderDesc.cuboid(size.x / 2, size.y / 2, size.z / 2).setTranslation(
      center.x,
      center.y,
      center.z,
    );
    if (rotZDeg !== 0) {
      const half = (rotZDeg * Math.PI) / 360;
      desc.setRotation({ x: 0, y: 0, z: Math.sin(half), w: Math.cos(half) });
    }
    const collider = this.world.createCollider(desc);
    this.levelColliders.push(collider);
    if (surface) this.surfaces.set(collider.handle, surface);
  }

  /**
   * Editor Play transition (A3): tear down every LEVEL collider/body (boxes, platforms) and
   * re-add them from the edited world data. The character body/controller are untouched — feel
   * state never resets with the level.
   *
   * ⚠️ IT RE-ADDS WHAT THE WORLD AUTHORED AND NOTHING ELSE. It used to re-add an implicit ground
   * plane as well, which is what made a Play transition drop the player to y = 0 on a world whose
   * real floor is somewhere else (`mount.ts`'s L3.C block has the measurement).
   */
  resetLevel(boxes: GymBox[], platforms: PlatformDef[]): void {
    for (const c of this.levelColliders) this.world.removeCollider(c, false);
    this.levelColliders.length = 0;
    this.surfaces.clear();
    // Removing the rigid body removes its attached collider too.
    for (const p of this.platforms.values()) this.world.removeRigidBody(p.body);
    this.platforms.clear();
    // S5: the entity bodies go with the level — the host rebinds them on its next `loadAll`
    // (`spawnEntities` in mount is the ONE site that does; B3's fence).
    this.clearEntityCollision();

    for (const b of boxes) {
      this.addBox(
        { x: b.pos[0], y: b.pos[1], z: b.pos[2] },
        { x: b.size[0], y: b.size[1], z: b.size[2] },
        b.rotZDeg ?? 0,
        b.surface,
      );
    }
    for (const def of platforms) this.addPlatform(def);
  }

  /** Moving platform: kinematic body + cuboid collider, pose driven by platformPosAt. */
  addPlatform(def: PlatformDef): void {
    const [x, y, z] = platformPosAt(def, 0);
    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(x, y, z),
    );
    const collider = this.world.createCollider(
      RAPIER.ColliderDesc.cuboid(def.size[0] / 2, def.size[1] / 2, def.size[2] / 2),
      body,
    );
    this.platforms.set(collider.handle, { def, body, delta: { x: 0, y: 0, z: 0 }, vel: { x: 0, y: 0, z: 0 } });
  }

  /**
   * Move every platform to its pose at `simTimeS` and step the world ONCE.
   *
   * ORDERING (chosen deliberately): the per-tick sequence is advancePlatforms → stepMotion →
   * moveCapsule. Platforms must be at their NEW pose before the character sweep resolves against
   * them, so when platforms exist the single world.step() per tick lives HERE, not in moveCapsule.
   * Rapier's scene-query structures only refresh on step, so a world whose owner never calls
   * advancePlatforms still needs stepping — moveCapsule keeps its historical post-sweep step for
   * exactly (and only) the platform-free case.
   */
  advancePlatforms(simTimeS: number): void {
    for (const p of this.platforms.values()) {
      const [x, y, z] = platformPosAt(p.def, simTimeS);
      const cur = p.body.translation();
      p.delta = { x: x - cur.x, y: y - cur.y, z: z - cur.z };
      p.vel = { x: p.delta.x / FIXED_DT, y: p.delta.y / FIXED_DT, z: p.delta.z / FIXED_DT };
      // TELEPORT (setTranslation), not setNextKinematicTranslation: the latter gives the body a
      // kinematic velocity and the character controller then drags a grounded capsule along with
      // it — intermittently, which double-applies the sim's ride delta. Teleporting keeps the
      // body's velocity zero, so the sim's platformDelta is the ONE authoritative ride path.
      p.body.setTranslation({ x, y, z }, false);
    }
    this.world.step();
  }

  moveCapsule(feetPos: V3, desired: V3): MoveResult {
    const center = { x: feetPos.x, y: feetPos.y + this.centerOffsetY, z: feetPos.z };
    this.body.setTranslation(center, false);
    this.collider.setTranslationWrtParent({ x: 0, y: 0, z: 0 });
    // Body→collider pose propagation normally happens inside world.step(); after a teleport
    // (respawn, test spawn) the sweep must not see the STALE collider pose — a capsule left
    // standing somewhere else reads as spuriously grounded for one tick.
    this.world.propagateModifiedBodyPositionsToColliders();

    this.controller.computeColliderMovement(this.collider, desired);
    const m = this.controller.computedMovement();
    const grounded = this.controller.computedGrounded();

    const next = { x: center.x + m.x, y: center.y + m.y, z: center.z + m.z };
    this.body.setNextKinematicTranslation(next);
    // Ordering (see advancePlatforms): when platforms exist, the ONE step per tick happens there,
    // BEFORE this sweep, so the sweep resolves against fresh platform poses. Without platforms
    // nothing else steps the world — and Rapier's scene-query structures only refresh on step —
    // so keep the historical post-sweep step for that case (platform-free headless tests).
    if (this.platforms.size === 0) this.world.step();

    // Ground identification: short downward ray from the NEW feet position (player excluded).
    // Resolves the platform we stand on (ride delta + inheritable velocity) and the surface tag.
    let platformDelta: V3 | null = null;
    let platformVel: V3 | null = null;
    let surface = 'default';
    if (grounded) {
      const feet = { x: feetPos.x + m.x, y: feetPos.y + m.y + 0.3, z: feetPos.z + m.z };
      const ray = new RAPIER.Ray(feet, { x: 0, y: -1, z: 0 });
      const hit = this.world.castRay(ray, 0.8, true, undefined, undefined, undefined, this.body);
      if (hit !== null) {
        const plat = this.platforms.get(hit.collider.handle);
        if (plat) {
          platformDelta = { ...plat.delta };
          platformVel = { ...plat.vel };
        }
        surface = this.surfaces.get(hit.collider.handle) ?? 'default';
      }
    }

    return { actual: { x: m.x, y: m.y, z: m.z }, grounded, platformDelta, platformVel, surface };
  }

  // ── S5 · static-mesh collision (bound by the HOST from what the entity published) ──

  /**
   * (Re)bind placement `index`: one FIXED body at the placement's pos/yaw, one collider per shape
   * with the scale baked into the points. `src === null` (the entity published nothing — a
   * placeholder, a class with no collision) removes any previous binding and reports nothing.
   * A degenerate hull (`convexHull` → null) warns once per asset, is skipped, and the OTHER shapes
   * still bind (rule 6: never a throw, never an all-or-nothing that leaves a whole crate hollow).
   * `trimesh` is built WITHOUT flags: `FIX_INTERNAL_EDGES` doubles the build and is a feel knob
   * for the Friday hands-on, not a default.
   */
  setEntityCollision(index: number, src: CollisionSource | null, pose: PlacementPose, asset = ''): void {
    this.removeEntityCollision(index);
    if (src === null) return;
    const t0 = performance.now();
    /*
     * ⭐ THE SCALE IS A TRIPLE NOW, AND THE TRANSFORM CAN REFUSE. A non-uniform scale is exact for
     * a hull and a trimesh, exact for a box whose scaled basis stays orthogonal, and impossible
     * for a `USP_` sphere / `UCP_` capsule (they become ellipsoids Rapier has no shape for). The
     * refused shape is dropped THERE and named HERE — into the same `warnings` list the degenerate
     * hull uses, which is what the Details panel prints as notes, so an artist who scales a
     * sphere-collided prop non-uniformly reads a sentence instead of walking through it.
     */
    const { shapes: scaled, refusals } = transformShapes(src.shapes, { pos: [0, 0, 0], yawDeg: 0, pitchDeg: 0, rollDeg: 0, scale: pose.scale });
    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.fixed()
        .setTranslation(pose.pos[0], pose.pos[1], pose.pos[2])
        .setRotation(placementRotation(pose)),
    );
    const colliders: RAPIER.Collider[] = [];
    const warnings = [...src.warnings, ...refusals];
    for (const r of refusals) {
      const key = `${asset}:${r}`;
      if (this.hullWarned.has(key)) continue;
      this.hullWarned.add(key);
      console.warn(`[physics] asset '${asset}': ${r}`);
    }
    for (const sh of scaled) {
      /*
       * ⚠️ rapier3d-compat 0.20's `ColliderDesc.convexHull` is typed `| null` but does not return
       * null for a degenerate cloud (collinear points): it hands back a desc whose raw shape is
       * null, and `createCollider` throws inside the WASM class assertion (measured in
       * `tests/rapierEntityCollision.test.ts`). So the refusal is caught HERE, per shape, and the
       * OTHER shapes still bind — rule 6, and never an all-or-nothing that leaves a whole crate
       * hollow because one hull was authored flat.
       */
      let collider: RAPIER.Collider | null = null;
      /*
       * ⚠️ `describeShape` IS INSIDE THE TRY, and it was not. It calls `ColliderDesc.convexHull` /
       * `.trimesh` — wasm, over caller-supplied Float32Arrays, and the exact call the comment above
       * says is unreliable. A throw there escaped `setEntityCollision`, escaped the `.then` in
       * mount's `bindCollision` as an unhandled rejection, left `this.entities` without a record,
       * and stranded the `body` created above in the physics world with no handle to remove it.
       * One degenerate hull under a scale drag leaked a body per pointer event.
       */
      try {
        const desc = this.describeShape(sh);
        if (desc !== null) collider = this.world.createCollider(desc, body);
      } catch {
        collider = null;
      }
      if (collider === null) {
        const key = `${asset}:${sh.kind}`;
        if (!this.hullWarned.has(key)) {
          this.hullWarned.add(key);
          console.warn(`[physics] asset '${asset}': a ${sh.kind === 'hull' ? 'UCX_ hull' : sh.kind} is degenerate (Rapier refused it) — skipped`);
        }
        warnings.push(sh.kind === 'hull' ? 'a UCX_ hull was degenerate and skipped' : `a ${sh.kind} shape was degenerate and skipped`);
        continue;
      }
      colliders.push(collider);
    }
    /*
     * L1.0: RAPIER'S OWN BUILD IS PUBLISHED RATHER THAN FOLDED AWAY. `buildMs` keeps its identity
     * (`extractMs + rapierMs`) so every existing reader is unaffected, but the two halves answer
     * different questions and only one of them a cook can touch: on Sponza the extraction was
     * 2,923 ms and this is ~170 ms, so a report that only had their sum could not have said which
     * end to optimise — and for a year it did not.
     */
    const rapierMs = performance.now() - t0;
    this.entities.set(index, {
      body,
      colliders,
      scaled,
      pose,
      info: {
        index,
        asset,
        mode: src.mode,
        from: src.from,
        shapes: colliders.length,
        tris: src.tris,
        colliders: colliders.length,
        buildMs: src.extractMs + rapierMs,
        walkMs: src.walkMs,
        yieldMs: src.yieldMs,
        rapierMs,
        // L2: carried through verbatim so `__fwg.state().collision.entities[i].origin` is the
        // headless proof that a cooked sidecar was actually consumed by the running game.
        origin: src.origin,
        refused: src.refused,
        warnings,
      },
    });
  }

  /**
   * Re-pose without rebuilding (pos/yaw only). Returns false when the SCALE changed — the caller
   * must `setEntityCollision` again — or when nothing is bound at that index. The colliders'
   * handles are unchanged by construction: this moves the body they hang off.
   */
  poseEntityCollision(index: number, pose: PlacementPose): boolean {
    const rec = this.entities.get(index);
    // Component-wise: `scale` is a triple since non-uniform scale landed, and `[2,2,2] !== [2,2,2]`
    // for two array literals — an identity compare here would rebuild 103 trimeshes per drag frame.
    if (!rec || !sameScale(rec.pose.scale, pose.scale)) return false;
    rec.body.setTranslation({ x: pose.pos[0], y: pose.pos[1], z: pose.pos[2] }, false);
    rec.body.setRotation(placementRotation(pose), false);
    rec.pose = pose;
    // A fixed body's colliders follow it only through propagation (the same reason `moveCapsule`
    // calls this after its teleport) — without it a query this tick sees the OLD pose.
    this.world.propagateModifiedBodyPositionsToColliders();
    return true;
  }

  removeEntityCollision(index: number): void {
    const rec = this.entities.get(index);
    if (!rec) return;
    this.world.removeRigidBody(rec.body); // takes its colliders with it
    this.entities.delete(index);
  }

  clearEntityCollision(): void {
    for (const rec of this.entities.values()) this.world.removeRigidBody(rec.body);
    this.entities.clear();
  }

  /**
   * The placement indices holding at least one collider, ascending — the residency gate's ONLY
   * question of this service, answered without building a report.
   *
   * ⚠️ IT EXISTS BECAUSE {@link entityCollisionInfo} WAS BEING USED FOR IT, AND IS THE WRONG
   * INSTRUMENT. That method spreads every record into two fresh objects and sorts the result, to
   * answer a question that needs neither. `mount.readResidency` asked it once per bind — on
   * `mars-hangar-01` that is 5,563 binds against a growing map, measured at ~0.34 ms a call and
   * ~12 s of the load between this and the fold above it. A list of integers should not allocate a
   * copy of the collision report.
   */
  boundEntityIndices(): number[] {
    const out: number[] = [];
    for (const [index, r] of this.entities) if (r.info.colliders > 0) out.push(index);
    // Ascending, because that is what the `entityCollisionInfo().sort()` this replaces produced and
    // a residency fold is not the place to discover an order dependency.
    return out.sort((a, b) => a - b);
  }

  /** What is bound, per placement, ascending by index — the Details line and `__fwg.state()`. */
  entityCollisionInfo(): EntityCollisionInfo[] {
    return [...this.entities.values()]
      .map((r) => ({ ...r.info, warnings: [...r.info.warnings] }))
      .sort((a, b) => a.index - b.index);
  }

  /** The bound shapes in WORLD space (the show flag's input) — posed from the body, per call. */
  entityCollisionShapes(): { index: number; shapes: CollisionShape[]; ucxMissingAt: Vec3 | null }[] {
    return [...this.entities.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([index, r]) => ({
        index,
        // Scale 1: `r.scaled` already has the placement's scale baked in, and a unit scale can
        // refuse nothing — so the overlay draws exactly the shapes the physics world holds.
        shapes: transformShapes(r.scaled, { pos: r.pose.pos, yawDeg: r.pose.yawDeg, pitchDeg: 0, rollDeg: 0, scale: [1, 1, 1] }).shapes,
        ucxMissingAt: r.info.warnings.some((w) => w.includes(UCX_NONE_FOUND)) ? r.pose.pos : null,
      }));
  }

  /** One shape → one `ColliderDesc` (null for a hull Rapier refuses). */
  private describeShape(sh: CollisionShape): RAPIER.ColliderDesc | null {
    switch (sh.kind) {
      case 'hull':
        return RAPIER.ColliderDesc.convexHull(sh.points);
      case 'box':
        return RAPIER.ColliderDesc.cuboid(sh.half[0], sh.half[1], sh.half[2])
          .setTranslation(sh.center[0], sh.center[1], sh.center[2])
          .setRotation({ x: sh.quat[0], y: sh.quat[1], z: sh.quat[2], w: sh.quat[3] });
      case 'sphere':
        return RAPIER.ColliderDesc.ball(sh.r).setTranslation(sh.center[0], sh.center[1], sh.center[2]);
      case 'capsule': {
        // Rapier's capsule is Y-aligned about its centre: pose it along a → b.
        const dx = sh.b[0] - sh.a[0];
        const dy = sh.b[1] - sh.a[1];
        const dz = sh.b[2] - sh.a[2];
        const len = Math.hypot(dx, dy, dz);
        const desc = RAPIER.ColliderDesc.capsule(len / 2, sh.r).setTranslation(
          (sh.a[0] + sh.b[0]) / 2,
          (sh.a[1] + sh.b[1]) / 2,
          (sh.a[2] + sh.b[2]) / 2,
        );
        if (len > 1e-6) desc.setRotation(quatFromYTo({ x: dx / len, y: dy / len, z: dz / len }));
        return desc;
      }
      case 'trimesh':
        return RAPIER.ColliderDesc.trimesh(sh.vertices, sh.indices);
    }
  }

  /**
   * Camera occlusion probe: allowed distance from `from` toward `to` before geometry, or null if
   * clear. Ray-based with a fixed margin for now; upgrade to a spherecast when Rapier's castShape
   * wrapper is wired (M3 follow-up).
   */
  cameraProbe(from: V3, to: V3): number | null {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dz = to.z - from.z;
    const dist = Math.hypot(dx, dy, dz);
    if (dist < 1e-4) return null;
    const dir = { x: dx / dist, y: dy / dist, z: dz / dist };
    const ray = new RAPIER.Ray(from, dir);
    const hit = this.world.castRay(ray, dist, true, undefined, undefined, undefined, this.body);
    if (hit === null) return null;
    return Math.max(hit.timeOfImpact - 0.35, 0.5);
  }

  groundAhead(feetPos: V3, dirXZ: V3, probeDist: number): boolean {
    const origin = {
      x: feetPos.x + dirXZ.x * probeDist,
      y: feetPos.y + 0.3,
      z: feetPos.z + dirXZ.z * probeDist,
    };
    const ray = new RAPIER.Ray(origin, { x: 0, y: -1, z: 0 });
    // 0.3 above feet + 0.5 below feet of standable reach.
    const hit = this.world.castRay(ray, 0.8, true, undefined, undefined, undefined, this.body);
    return hit !== null;
  }
}
